import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';

import { apiUrlGenerator } from '../use-api';
import { showResult } from '../utils';

import type { IValue } from 'components/atoms/base-range-datepicker';
import type { IPatientSnapshot } from 'infrastructure/interfaces';

const getUrl = (guid: string) => `core-2/patients/${guid}/snapshot`;

interface IProps {
  patientGuid: string;
  dates: IValue | null;
}
interface IDataProps {
  patientGuid: string;
  startTime: string | undefined;
  endTime: string | undefined;
}

const getPatientSnapshotKey = (props: IProps) => ['getPatientSnapshot', props];

const queryFn = async ({ patientGuid, ...props }: IDataProps) => {
  const url = getUrl(patientGuid);

  const { data, error } = await dataService.getList<IPatientSnapshot>(
    apiUrlGenerator(url, props),
  );

  if (error) {
    return Promise.reject(error);
  }

  return data;
};

export const useGetPatientSnapshot = ({ patientGuid, dates }: IProps) => {
  const startTime = dates?.[0]?.getTime().toString();
  const endTime = dates?.[1]?.getTime().toString();

  const queryKey = getPatientSnapshotKey({ patientGuid, dates });

  const { data, isLoading, error } = useQuery<
    IPatientSnapshot | undefined,
    string | undefined
  >({
    queryKey,
    queryFn: async () => queryFn({ patientGuid, startTime, endTime }),
    enabled: !!startTime && !!endTime,
  });

  useEffect(() => {
    if (error) {
      showResult(error);
    }
  }, [error]);

  return {
    patientSnapshot: data,
    isLoading,
  };
};
